/** Where a staff member approves Claude connecting to ClientFlow as them (after signing in with two-step verification). */
import { AI_NEVER } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ShieldCheck, Sparkles, X } from "lucide-react";
import { Brand, Loading } from "@/components/page";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";

type Check = { appName: string; firmName: string; userName: string; mode: "off" | "read" | "propose"; blocked: string | null };

export function OAuthConsentPage() {
  const params = Object.fromEntries(new URLSearchParams(window.location.search));
  const keep = ["response_type", "client_id", "redirect_uri", "code_challenge", "code_challenge_method", "state", "scope", "resource"];
  const q = new URLSearchParams(Object.entries(params).filter(([k]) => keep.includes(k))).toString();
  const { data, error, isLoading } = useQuery<Check>({ queryKey: [`/api/oauth/authorize/check?${q}`], retry: false });
  const decide = useMutation({
    mutationFn: (decision: "allow" | "deny") => api.send<{ redirect: string }>("POST", "/api/oauth/authorize", { ...Object.fromEntries(Object.entries(params).filter(([k]) => keep.includes(k))), decision }),
    onSuccess: (r) => window.location.assign(r.redirect),
  });
  return (
    <main className="flex min-h-screen items-start justify-center bg-muted/30 px-4 py-10 sm:items-center">
      <div className="w-full max-w-md rounded-xl border border-card-border bg-card p-6 shadow-[var(--card-shadow)]">
        <div className="mb-5">
          <Brand />
        </div>
        {isLoading ? (
          <Loading />
        ) : error || !data ? (
          <div className="space-y-2">
            <h1 className="text-lg font-semibold">This connection can't continue</h1>
            <p className="text-sm text-muted-foreground">{errorMessage(error)}</p>
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <h1 className="flex items-center gap-2 text-lg font-semibold">
                <Sparkles className="h-5 w-5 text-primary" /> Connect {data.appName} to ClientFlow
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {data.appName} will work as you, <span className="font-medium text-foreground">{data.userName}</span>, at <span className="font-medium text-foreground">{data.firmName}</span>.
              </p>
            </div>
            {data.blocked ? (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">{data.blocked}</p>
            ) : (
              <>
                <div className="space-y-2 text-sm">
                  <p className="font-medium">It will be able to</p>
                  <p className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> See and explain what you can see in ClientFlow, for clients who have signed the AI consent.
                  </p>
                  {data.mode === "propose" ? (
                    <p className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Propose actions. Each waits in ClientFlow's Review inbox until a person approves it.
                    </p>
                  ) : (
                    <p className="flex items-start gap-2">
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /> Propose changes: not yet (read-only for now).
                    </p>
                  )}
                </div>
                <div className="space-y-1.5 text-sm">
                  <p className="font-medium">It can never</p>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    <li>Change anything without a person's approval in ClientFlow</li>
                    {AI_NEVER.slice(0, 5).map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                </div>
                <p className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  Everything it looks at is recorded in AI activity. Disconnect any time in Firm Settings › Data and security › AI.
                </p>
              </>
            )}
            {decide.error && <p className="text-sm text-destructive">{errorMessage(decide.error)}</p>}
            <div className="flex flex-wrap gap-2">
              {!data.blocked && (
                <Button disabled={decide.isPending} onClick={() => decide.mutate("allow")}>
                  Allow
                </Button>
              )}
              <Button variant="outline" disabled={decide.isPending} onClick={() => decide.mutate("deny")}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
